import React, { MouseEvent, useCallback } from 'react';

import t from '../../../labels/labelling-utils';
import { IComponent } from '../../../typings/generic-component';
import { MatchStatusList, MatchStatusType, MatchPhaseRound } from '../../../typings/match';
import {
  isAbandoned,
  isCancelled,
  isEnded,
  isEndedAfterOverTime,
  isEndedAfterShootout,
  isLive,
  isPostponed,
} from '../../../utils/match-status';
import IconPlay from '../../atoms/icons/icon-play';
import { getUrlStatic } from '../../../utils/placeholders-football';
import { makeClassNames } from '../../../utils/styles';
import { dateFormat } from '../../../utils/times';
import { objectToDataAttributes } from '../../../utils/tracking-data';
import { CompetitionBoardCardPreview, ImgPlaceholder, Text } from '../../atoms';
import Label from '../../atoms/label/label';
import { ICustomTrackingData } from '../../../typings/competition-board';

import './competition-board-card.scss';
import { getScoreWeight, getStatusLabel, getTeamName, getCardHeader } from './competition-board-card.utils';

const placeholder = `https://${getUrlStatic()}/images/skysport/configurations/placeholders/football/team-logo.png`;

interface ICompetitionBoardCardClasses {
  wrapper: string;
  boardCard: string;
  header: string;
  title: string;
  matchInfo: string;
  matchStatus: string;
  matchInfoDot: string;
  content: string;
  teamContainer: string;
  teamContainerBorder: string;
  teamRow: string;
  prevMatch: string;
  teamRowWinner: string;
  teamName: string;
  teamInfo: string;
  teamImageContainer: string;
  score: string;
  boardCardLinkedMatch: string;
  linkedMatchData: string;
  linkedMatchScore: string;
  linkedMatchIconPlay: string;
  linkedMatchLabel: string;
}

export type Winner = 'home' | 'away';

export const winnerList: Winner[] = ['home', 'away'];

export const statusList: MatchStatusType[] = MatchStatusList;

export interface ITeam {
  id?: string | null;
  name?: string | null;
  shortName?: string | null;
  image?: string | null;
  goal?: number;
  shootoutGoal?: number;
  prevMatch?: { home: ITeam; away: ITeam; winner?: Winner };
}

export interface ILinkedTeam {
  name?: string | null;
  shortName?: string | null;
  goal?: number;
}

export interface ILinkedMatch {
  home: ILinkedTeam;
  away: ILinkedTeam;
  competitionPhaseRound: MatchPhaseRound;
  status: MatchStatusType;
  date: string;
  link?: string;
}
export interface ICompetitionBoardCard extends IComponent {
  title: string;
  home: ITeam;
  away: ITeam;
  hidePrevMatches?: boolean;
  winner?: Winner;
  status: MatchStatusType;
  needConnector?: boolean;
  link?: string;
  onPreviewClick?: () => void;
  linkedMatch?: ILinkedMatch;
  trackingData?: ICustomTrackingData;
}

const classNames = (
  needConnector: ICompetitionBoardCard['needConnector'],
  link?: ICompetitionBoardCard['link'],
  hidePrevMatches: ICompetitionBoardCard['hidePrevMatches'] = false,
  linkedMatch: boolean = false
): ICompetitionBoardCardClasses =>
  makeClassNames({
    wrapper: ['competition-board-card_wrapper', hidePrevMatches && 'competition-board-card_wrapper--hide-prev'],
    boardCard: ['competition-board-card', link && 'competition-board-card--show-animation'],
    header: 'competition-board-card__header',
    title: 'competition-board-card__title',
    content: ['competition-board-card__content', needConnector && 'competition-board-card__content--connector'],
    teamContainer: 'competition-board-card__team-container',
    teamContainerBorder: ['competition-board-card__team-container', 'competition-board-card__team-container--border'],
    teamRow: 'competition-board-card__team-row',
    prevMatch: 'competition-board-card__prev-match',
    teamRowWinner: ['competition-board-card__team-row', 'competition-board-card__team-row--winner'],
    teamName: 'competition-board-card__team-name',
    teamInfo: 'competition-board-card__team-info',
    teamImageContainer: 'competition-board-card__team-image-container',
    score: 'competition-board-card__score',
    boardCardLinkedMatch: [
      'competition-board-card__linked-match',
      linkedMatch && 'competition-board-card__linked-match-data--show-animation',
    ],
    linkedMatchData: 'competition-board-card__linked-match-data',
    linkedMatchScore: 'competition-board-card__linked-match-score',
    linkedMatchIconPlay: 'competition-board-card__linked-match-icon-play',
    linkedMatchLabel: 'competition-board-card__linked-match-label',
  }) as ICompetitionBoardCardClasses;

const CompetitionBoardCard = ({
  home,
  away,
  winner,
  status,
  title,
  needConnector,
  link,
  trackingData,
  hidePrevMatches = false,
  onPreviewClick,
  linkedMatch,
}: ICompetitionBoardCard) => {
  const cn = classNames(needConnector, link, hidePrevMatches, Boolean(linkedMatch));

  const handlePreviewClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onPreviewClick?.();
    },
    [onPreviewClick]
  );

  return (
    <div className={cn.wrapper}>
      <a
        href={link || '#'}
        tabIndex={0}
        className={cn.boardCard}
        title={`${home.name} - ${away.name}`}
        {...(trackingData?.mainLink ? objectToDataAttributes({ intcmp: trackingData.mainLink }) : {})}
        onClick={(e) => {
          if (!link) {
            e.preventDefault();
          }
        }}
      >
        <div className={cn.header}>
          <Text className={cn.title} fontSize="12" fontWeight="500" tag="p">
            {getCardHeader(status, title)}
          </Text>
          {(isLive(status) ||
            isEndedAfterOverTime(status) ||
            isEndedAfterShootout(status) ||
            isAbandoned(status) ||
            isPostponed(status) ||
            isCancelled(status)) && (
            <Label
              label={getStatusLabel(status) || ''}
              isCardLabel
              isBlackText={!isLive(status) && !isAbandoned(status) && !isPostponed(status) && !isCancelled(status)}
              isLive={isLive(status)}
              noRounded
            />
          )}
        </div>
        <div className={cn.content}>
          <div className={winner === 'home' ? cn.teamRowWinner : cn.teamRow}>
            {!hidePrevMatches && (
              <div className={cn.prevMatch}>
                <CompetitionBoardCardPreview
                  onClick={handlePreviewClick}
                  home={home.prevMatch?.home}
                  away={home.prevMatch?.away}
                  winner={home.prevMatch?.winner}
                />
              </div>
            )}
            <div className={cn.teamContainerBorder}>
              <div className={cn.teamInfo}>
                <div className={cn.teamImageContainer}>
                  <ImgPlaceholder
                    title={home?.name || ''}
                    alt={home?.name || ''}
                    src={home?.image || undefined}
                    placeholder={placeholder}
                  />
                </div>
                <Text
                  className={cn.teamName}
                  tag="p"
                  fontSize="16"
                  color="blue"
                  fontWeight={getScoreWeight({ team: 'home', winner })}
                >
                  {home?.shortName ? getTeamName(home?.shortName) : t('competition.board.noname')}
                </Text>
              </div>
              {isEnded(status) && (
                <div className={cn.score}>
                  <Text tag="span" fontSize="16" color="blue" fontWeight={getScoreWeight({ team: 'home', winner })}>
                    {home?.goal}
                  </Text>
                  {isEndedAfterShootout(status) && home?.shootoutGoal ? (
                    <Text tag="span" fontSize="16" color="blue" fontWeight={getScoreWeight({ team: 'home', winner })}>
                      ({home?.shootoutGoal})
                    </Text>
                  ) : null}
                </div>
              )}
            </div>
          </div>
          <div className={winner === 'away' ? cn.teamRowWinner : cn.teamRow}>
            {!hidePrevMatches && (
              <div className={cn.prevMatch}>
                <CompetitionBoardCardPreview
                  onClick={handlePreviewClick}
                  home={away.prevMatch?.home}
                  away={away.prevMatch?.away}
                  winner={away.prevMatch?.winner}
                />
              </div>
            )}
            <div className={cn.teamContainer}>
              <div className={cn.teamInfo}>
                <div className={cn.teamImageContainer}>
                  <ImgPlaceholder
                    title={away?.name || ''}
                    alt={away?.name || ''}
                    src={away?.image || undefined}
                    placeholder={placeholder}
                  />
                </div>
                <Text
                  className={cn.teamName}
                  tag="p"
                  fontSize="16"
                  color="blue"
                  fontWeight={getScoreWeight({ team: 'away', winner })}
                >
                  {away?.shortName ? getTeamName(away?.shortName) : t('competition.board.noname')}
                </Text>
              </div>
              {isEnded(status) && (
                <div className={cn.score}>
                  <Text tag="span" fontSize="16" color="blue" fontWeight={getScoreWeight({ team: 'away', winner })}>
                    {away?.goal}
                  </Text>
                  {isEndedAfterShootout(status) && away?.shootoutGoal ? (
                    <Text tag="span" fontSize="16" color="blue" fontWeight={getScoreWeight({ team: 'away', winner })}>
                      ({away?.shootoutGoal})
                    </Text>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </a>
      {linkedMatch ? (
        <a
          href={linkedMatch.link || '#'}
          tabIndex={0}
          title={`${linkedMatch.home.name} - ${linkedMatch.away.name}`}
          className={cn.boardCardLinkedMatch}
          {...(trackingData?.mainLink ? objectToDataAttributes({ intcmp: trackingData.detailLink }) : {})}
          onClick={(e) => {
            if (!linkedMatch.link) {
              e.preventDefault();
            }
          }}
        >
          <div className={cn.linkedMatchData}>
            <div className={cn.linkedMatchLabel}>
              <Text fontSize="12" fontWeight="500" tag="span">
                {linkedMatch.competitionPhaseRound}:
              </Text>
            </div>

            {!isEnded(linkedMatch.status) ? (
              <div className={cn.linkedMatchLabel}>
                <Text fontSize="12" fontWeight="500" tag="span">
                  {`${dateFormat({ date: linkedMatch.date, format: 'ddd D MMM' })} | ore ${dateFormat({
                    date: linkedMatch.date,
                    format: 'HH:mm',
                  })}`}
                </Text>
              </div>
            ) : (
              <div className={cn.linkedMatchScore}>
                <Text fontSize="14" fontWeight="400">
                  {linkedMatch.home.shortName}
                </Text>{' '}
                <Text fontSize="14" fontWeight="500">
                  {linkedMatch.home.goal}
                </Text>
                <Text fontSize="14" fontWeight="500">
                  {' '}
                  -{' '}
                </Text>
                <Text fontSize="14" fontWeight="500">
                  {linkedMatch.away.goal}
                </Text>{' '}
                <Text fontSize="14" fontWeight="400">
                  {linkedMatch.away.shortName}
                </Text>
              </div>
            )}
          </div>
          {isEnded(linkedMatch.status) ? (
            <div className={cn.linkedMatchIconPlay}>
              <IconPlay size="small" color="light" />
            </div>
          ) : null}
        </a>
      ) : null}
    </div>
  );
};

export default CompetitionBoardCard;
